/**
 * Capabilities of the Electron shell hosting this renderer.
 *
 * The renderer is deployed continuously (web) while the shell rolls out
 * through the auto-updater over days: at any time the fleet runs a mix of
 * shell versions, and the renderer must work on all of them. Every new
 * main-process feature must therefore be exposed as a named method on the
 * preload bridge — the preload ships with the shell, so the method's presence
 * IS the shell's capability — detected here, with a fallback on the legacy
 * protocol when absent. Never gate a feature on a version comparison, and
 * never remove or repurpose an existing IPC channel on the main side: only add.
 *
 * The capability set is immutable for the lifetime of a renderer process
 * (applying a shell update restarts the whole app): checks can be called at
 * any time, and features light up on the restart that installs the update.
 */

/** The preload bridge, looked up by method name (absent outside Electron). */
const bridge = (): Record<string, unknown> =>
    (window.ipcRenderer ?? {}) as unknown as Record<string, unknown>;

/**
 * Whether the bridge exposes every given method.
 *
 * In dev, `localStorage.setItem('forceLegacyShell', '1')` simulates an old
 * shell to test the legacy fallbacks without installing an old build.
 */
function hasBridgeMethods(...names: string[]): boolean {
    if (import.meta.env.DEV && localStorage.getItem('forceLegacyShell') === '1') {
        return false;
    }

    return names.every((name) => typeof bridge()[name] === 'function');
}

/**
 * Window placement primitives: notification drag & drop and edge snapping.
 *
 * On older shells the main process positions the notification window itself
 * (right edge, position presets) and dragging is unavailable.
 */
export function supportsWindowPlacement(): boolean {
    return hasBridgeMethods(
        'getScreenContext',
        'getNotificationWindowBounds',
        'setNotificationWindowBounds',
        'dragOverlayShow',
        'dragOverlayUpdate',
        'dragOverlayHide',
    );
}

/**
 * On-demand Vitale card reading from the notification window.
 *
 * Older shells only read the card through the polling detection loop: the
 * on-demand read UI (sliding tab, collapsed bubble, empty-state button) is
 * hidden there instead of failing on click.
 */
export function supportsOnDemandCardRead(): boolean {
    return hasBridgeMethods('readVitaleCard');
}

/**
 * Workstation identity (ScreenConnect session, private IP, MAC) sent with the
 * host registration.
 *
 * Older shells register the host with its name and version only.
 */
export function supportsMachineIdentity(): boolean {
    return hasBridgeMethods('getMachineIdentity');
}

/**
 * LIVE — passive detection of the patient selected at the counter, by listening
 * to this workstation's own LGO network traffic.
 *
 * Older shells cannot capture traffic, so the renderer keeps the LIVE context to
 * the on-demand card read there instead of starting a watch that never fires.
 */
export function supportsLivePatientWatch(): boolean {
    return hasBridgeMethods('startLivePatientWatch', 'stopLivePatientWatch', 'onLivePatientSelected');
}

/**
 * LIVE — the operator who takes the counter, read from the same traffic.
 *
 * Older shells cannot report it: no operator is shown there.
 */
export function supportsLiveOperator(): boolean {
    return hasBridgeMethods('onLiveOperatorChanged');
}

/**
 * LIVE — the Apodis Live service can be asked to update itself, and the watch
 * status tells its version, which the host registration reports.
 *
 * Older shells cannot: the service stays as it is there, and the host's
 * recorded version is left untouched.
 */
export function supportsLiveServiceUpdate(): boolean {
    return hasBridgeMethods('updateLiveService');
}

/**
 * LIVE — the shell can install the Apodis Live service itself, through the
 * Windows authorization prompt, and replace one that cannot update itself.
 *
 * Older shells cannot: the activation is not offered there, and the service
 * comes only with the Apodis Suite installer.
 */
export function supportsLiveServiceInstall(): boolean {
    return hasBridgeMethods('installLiveService', 'getLivePatientWatchStatus');
}

/**
 * LIVE — whether Apodis Live works on this workstation can be asked, so the
 * settings offer the choice between LIVE and the OCR.
 *
 * Older shells cannot say it: the choice stays hidden there, and the
 * detection works as before.
 */
export function supportsLiveDetectionMode(): boolean {
    return hasBridgeMethods('getLivePatientWatchStatus');
}

/**
 * Click-through over the transparent areas of the notification window.
 *
 * A click-through window receives no mouse message of its own, so the
 * renderer can only flip the flag back if it can read the OS cursor
 * position: the capability requires the cursor and bounds primitives too,
 * never the forwarded mouse moves alone (a global mouse hook that stays
 * silent under remote-control tools). A shell that cannot be watched keeps
 * the whole window click-blocking instead of risking a deaf window.
 */
export function supportsClickThrough(): boolean {
    return hasBridgeMethods(
        'setNotificationClickThrough',
        'getScreenContext',
        'getNotificationWindowBounds',
    );
}

/**
 * The shell detection loop can be handed over to the renderer.
 *
 * Groundwork for a fully web-deployed detection pipeline: a renderer driving
 * its own loop (fed by the raw `detection:capture` primitive) must claim
 * ownership at every boot — and only on shells exposing this method, so the
 * shell loop provably goes dormant instead of double-detecting.
 */
export function supportsDetectionOwnership(): boolean {
    return hasBridgeMethods('setDetectionOwner');
}

/**
 * CPS card presence probing, used to confirm this workstation can hold the
 * amelipro session. Older shells cannot reach the reader's CPS slot, so the
 * amelipro auto-connection setting stays hidden there.
 */
export function supportsCpsDetection(): boolean {
    return hasBridgeMethods('detectCpsCard');
}

/**
 * Whether the shell can hold an amelipro session and query ADAC.
 */
export function supportsAmelipro(): boolean {
    return hasBridgeMethods('ameliproConnect', 'ameliproStatus', 'ameliproQueryAdac');
}

/**
 * The ghost sign-in: the shell clicks the CPS entry of the identity provider
 * itself, so the pharmacist only answers the card middleware's own prompts.
 *
 * Older shells can only reveal the amelipro window and let the user sign in by
 * hand, so the configurator's code verification stays hidden there.
 */
export function supportsAmeliproCpsAuthentication(): boolean {
    return hasBridgeMethods('ameliproAuthenticateCps');
}

/**
 * TEMPORARY — the shell pushes the amelipro cycle's steps as they happen, for
 * the ADAC debugging aid. Older shells say nothing; the aid then shows only
 * what comes back with the result.
 */
export function supportsAmeliproProgress(): boolean {
    return hasBridgeMethods('onAmeliproProgress');
}

/**
 * The shell can keep a code porteur in the operating system's credential
 * store — the Windows Credential Manager on the pharmacies' machines.
 *
 * Older shells have nowhere safe to put it, so the configurator keeps its save
 * disabled there rather than pretending the code was kept.
 */
export function supportsCpsCodeVault(): boolean {
    return hasBridgeMethods('storeCpsCode', 'hasCpsCode', 'forgetCpsCode');
}

/**
 * Everything the ADAC module asks of its shell: reading the CPS card, keeping
 * the code porteur in the operating system's credential store, and holding the
 * amelipro session it queries.
 *
 * The renderer ships continuously while shells roll out over days, so a
 * pharmacy can activate the module before its Connect is able to carry it. The
 * screens say so plainly rather than half-working — a configuration that reads
 * "no card found" on a Connect that cannot look for one sends the pharmacist
 * hunting for a problem that is not theirs.
 */
export function supportsAdac(): boolean {
    return supportsCpsDetection()
        && supportsAmelipro()
        && supportsAmeliproCpsAuthentication()
        && supportsCpsCodeVault();
}

/**
 * The renderer can drive the shell update: check right away, follow the
 * download and restart on the new version from its own interface.
 *
 * Older shells only check on their own every one to two hours and ask for the
 * restart through their native dialog, so the renderer can merely schedule the
 * update on the server and tell how long it may take.
 */
export function supportsShellUpdate(): boolean {
    return hasBridgeMethods(
        'checkForShellUpdate',
        'getShellUpdateStatus',
        'onShellUpdateStatus',
        'installShellUpdate',
        'setShellUpdatePromptOwner',
    );
}
